// meta selector combining different state trees like customer, order, revenueCenter, etc.
import { createSelector } from 'reselect'
import { get, sortBy, sortedUniqBy, filter, some, find, values } from 'lodash'
import generateUuid from 'uuid/v4'
import { toast } from 'react-toastify'

import { makeGetAttendantStandUuids } from './attendant'
import {
  getActiveCustomerId,
  getAllOrderingPreferences,
  makeGetCustomer,
  makeGetCustomerOrderingPreferences,
  makeGetCustomerPayments,
} from './customer'
import { getCurrentEventId } from './events'
import { makeGetTabOrders, makeGetTabOrder, makeGetOrder } from './order'
import { makeGetRevenueCenter, getActiveRevenueCenterId } from './revenueCenter'

import makeCustomerPreferences from '../utils/customerPreferences'

const filteredOrdersForAttendant = (state, orders) => {
  const getAttendantStandUuids = makeGetAttendantStandUuids()
  const attendantStandUuids = getAttendantStandUuids(state)
  const currentEventId = getCurrentEventId(state)

  return filter(orders, (o) => {
    return (
      attendantStandUuids.includes(o.standUuid) &&
      o.salesEventId === currentEventId
    )
  })
}

export const getDisplaySuitesFromPreorders = (state) => {
  const getTabOrders = makeGetTabOrders()
  const sortedOrders = getTabOrders(state)
  const attendantOrders = filteredOrdersForAttendant(state, sortedOrders)

  const getCustomer = makeGetCustomer()
  const getRevenueCenter = makeGetRevenueCenter()

  return attendantOrders.map((order) => {
    const customerId = order.customerId
    const customer = getCustomer(state, customerId) || {}
    const customerName = get(customer, 'name', 'Anonymous')

    const revenueCenterId = order.standUuid
    const rvc = getRevenueCenter(state, revenueCenterId) || {}
    const rvcName = get(rvc, 'name', 'No Suite')

    const displayName = `${rvcName} ${customerName}`

    return { customerId, customerName, revenueCenterId, displayName: displayName }
  })
}

export const getDisplaySuitesFromStandPreferences = (state, preferences) => {
  const allPreferences = preferences ?? getAllOrderingPreferences(state)
  const currentEventId = getCurrentEventId(state)

  const getAttendantStandUuids = makeGetAttendantStandUuids()
  const attendantStandUuids = getAttendantStandUuids(state)

  const standPreferences = filter(allPreferences, (pref) => {
    return (
      pref.type === 'Customer::StandPreference' &&
      pref.salesEventId === currentEventId &&
      attendantStandUuids.includes(pref.standUuid)
    )
  })

  const getCustomer = makeGetCustomer()
  const getRevenueCenter = makeGetRevenueCenter()

  return standPreferences.map((pref) => {
    const customerId = pref.customerId
    const customer = getCustomer(state, customerId) || {}
    const customerName = get(customer, 'name', 'Anonymous')
    const settingsDisabled = customerName === 'Anonymous'

    const revenueCenterId = pref.standUuid
    const rvc = getRevenueCenter(state, revenueCenterId) || {}
    const rvcName = get(rvc, 'name', 'No Suite')

    const displayName = `${rvcName} ${customerName}`

    return { customerId, customerName, revenueCenterId, displayName: displayName, settingsDisabled }
  })
}

export const getDisplaySuites = (state, preferences) => {
  const suiteGroups = [
    ...getDisplaySuitesFromPreorders(state),
    ...getDisplaySuitesFromStandPreferences(state, preferences),
  ]

  // This line is why we are unable to make several anonymous customers
  // for a single suite. I don't know if we want to maybe concat a count
  // to each anonymous user such as : Anonymous (1), Anonymous (2)
  return sortedUniqBy(sortBy(suiteGroups, 'displayName'), 'displayName')
}

export const makeGetDisplaySuites = () => createSelector(
  [getDisplaySuites, makeGetCustomer, makeGetTabOrders, makeGetRevenueCenter, makeGetAttendantStandUuids],
  (suites) => suites
)

export const suiteCustomerIsOrdering = (state) => {
  const currentCustomerId = get(state, 'customer.activeCustomerId')
  const currentRevenueCenterId = get(state, 'revenueCenter.activeRevenueCenter')

  return !!currentCustomerId && !!currentRevenueCenterId
}

const getDisplaySuiteByOrder = (state, orderId) => {
  const getTabOrder = makeGetTabOrder()
  const order = getTabOrder(state, orderId)

  const getCustomer = makeGetCustomer()
  const getRevenueCenter = makeGetRevenueCenter()

  const customerId = order.customerId
  const customer = getCustomer(state, customerId) || {}
  const customerName = get(customer, 'name', 'Anonymous')
  const settingsDisabled = customerName === 'Anonymous'

  const revenueCenterId = order.standUuid
  const rvc = getRevenueCenter(state, revenueCenterId) || {}
  const rvcName = get(rvc, 'name', 'No Suite')

  const displayName = `${rvcName} - ${customerName}`

  return { customerId, customerName, revenueCenterId, displayName: displayName, settingsDisabled }
}

export const makeGetDisplaySuiteByOrder = () => createSelector(
  [getDisplaySuiteByOrder],
  (suite) => suite
)

const getAllPayments = (state) => values(get(state, 'customerPayment.byId', {}))

export const getPaymentByOrder = (state, orderId) => {
  const getOrder = makeGetOrder()
  const order = getOrder(state, orderId)
  const { customerPaymentId } = order
  if (!customerPaymentId) return
  const allPayments = getAllPayments(state)
  const result = find(allPayments, (payment) => customerPaymentId === payment?.uuid)
  if (!result) {
    toast.error("Order payment preference could not be found")
  }
  return result
}

const getOrderPreferencesByOrder = (state, orderId) => {
  const getOrder = makeGetOrder()
  const getCustomerPayments = makeGetCustomerPayments()
  const getCustomerPreferences = makeGetCustomerOrderingPreferences()
  const order = getOrder(state, orderId)

  const { customerId, standUuid } = order

  const payments = getCustomerPayments(state, customerId)
  const preferences = getCustomerPreferences(state, customerId, standUuid)

  const result = makeCustomerPreferences(preferences, payments)

  return result
}

export const makeGetOrderPreferencesByOrder = () => createSelector(
  getOrderPreferencesByOrder,
  (orderPreferences) => orderPreferences
)

// TODO: change this to getCustomerPreferences
const getActiveCustomerPreferences = (state, customerId, revenueCenterId) => {
  if (!customerId) customerId = getActiveCustomerId(state)
  if (!revenueCenterId) revenueCenterId = getActiveRevenueCenterId(state)

  const getCustomerPayments = makeGetCustomerPayments()
  const getCustomerPreferences = makeGetCustomerOrderingPreferences()

  const payments = getCustomerPayments(state, customerId)
  const preferences = getCustomerPreferences(state, customerId, revenueCenterId)

  const result = makeCustomerPreferences(preferences, payments)

  return result
}

export const makeGetActiveCustomerPreferences = () => createSelector(
  getActiveCustomerPreferences,
  (orderPreferences) => orderPreferences
)

export const getDefaultOrderingPreferences = (state) => get(state, `orderingPreference.defaultPreferences.byStandUuid`, {})

export const getSuiteCustomers = (state, suiteInformation) => {
  const displaySuites = getDisplaySuites(state)
  const suiteCustomers = filter(displaySuites, (suite) => suite.revenueCenterId === suiteInformation.revenueCenterId && suite.customerId !== suiteInformation.customerId )
  const anonExists = some(suiteCustomers, (suite) => suite.displayName.includes("Anonymous"))
  const customerIsAnon = (suiteInformation?.customerName ?? "").includes("Anonymous")

  const anonymousCustomer = !anonExists && !customerIsAnon ? [{
    displayName: 'Anonymous',
    customerName: 'Anonymous',
    customerId: generateUuid(),
    revenueCenterId: suiteInformation.revenueCenterId
  }] : []

  return [ ...suiteCustomers, ...anonymousCustomer ]
}
